import { useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { industriesList } from "@/mocks/industriesPageData";

export default function IndustriesPage() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Industries" },
        ]}
      />

      <PageHero
        badge="Industry Solutions"
        headline="Technology Solutions Across Industries"
        subtext="Build digital products designed around the unique needs of your industry."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />

      {/* Industries Grid */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Industries We Serve
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {industriesList.map((industry, i) => (
              <AnimatedSection key={industry.slug} delay={i * 100}>
                <a
                  href={`/industries/${industry.slug}`}
                  className="group relative block rounded-2xl overflow-hidden h-[320px] md:h-[380px] border border-background-200/60 hover:border-accent-300/30 hover:shadow-xl hover:shadow-accent-500/10 transition-all duration-500 card-lift"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <img
                    src={industry.image}
                    alt={industry.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/50 to-primary-950/20 transition-all duration-500 ${hoveredIndex === i ? "from-primary-950/95 via-primary-950/70" : ""}`} />

                  <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-500/20 text-accent-300 mb-4 backdrop-blur-sm border border-accent-500/20">
                      <i className={`${industry.icon} text-xl`} />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-2 font-heading">
                      {industry.name}
                    </h3>
                    <p className="text-sm text-background-300 leading-relaxed mb-4 max-w-xs">
                      {industry.tagline}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-400 group-hover:text-accent-300 transition-colors">
                      Explore
                      <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </a>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Highlight */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Technology That Understands Your Industry
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { title: "Business Understanding", desc: "We take time to understand your industry's unique challenges and workflows.", icon: "ri-building-4-line" },
              { title: "Technical Expertise", desc: "Deep experience building solutions for your specific domain.", icon: "ri-code-s-slash-line" },
              { title: "Scalable Architecture", desc: "Systems designed to grow as your industry demands evolve.", icon: "ri-expand-diagonal-line" },
              { title: "Long-Term Support", desc: "Ongoing partnership to adapt technology as regulations and markets change.", icon: "ri-heart-3-line" },
            ].map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}